import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, Plus, Trash2, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { PageHeader } from "@/components/page-header";
import { ReasonDialog } from "@/components/reason-dialog";
import { ErrorState, Loading } from "@/components/query-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { PolicyMentionTextarea } from "@/components/policy-mention-textarea";
import type {
  AgentAudience, GeneralContextCheckName, GeneralContextContent, GeneralContextDetail, GeneralContextExport,
  GeneralContextReport, GeneralContextRevision, GeneralContextState, GlossaryEntry,
} from "@/lib/types";

type Tab = "edit" | "preview" | "review" | "publish" | "history";
const TABS: Tab[] = ["edit", "preview", "review", "publish", "history"];
const CHECKS: GeneralContextCheckName[] = ["schema", "budget", "references", "injection", "exposure", "conflict", "rubric"];

const emptyContent = (): GeneralContextContent => ({ glossary: [], base: { rider: "", staff: "" }, details: [] });
const AUDIENCES = ["rider", "staff"] as const;
/** The rendered block adds a fixed preamble and tags around the Base text. */
const PREAMBLE_BYTES = 290;
const utf8Bytes = (value: string) => new TextEncoder().encode(value).length;
/** Portuguese text averages close to 4 bytes per token in the models we use. */
const approxTokens = (bytes: number) => Math.round(bytes / 4);
const lines = (value: string) => value.split("\n").map((item) => item.trim()).filter(Boolean);
const commas = (value: string) => value.split(",").map((item) => item.trim()).filter(Boolean);

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as { issues?: { path: string; message: string }[] } | undefined;
    const issues = body?.issues?.map((issue) => `${issue.path}: ${issue.message}`).join("; ");
    return issues ? `${error.message} — ${issues}` : error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

export function GeneralContextPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("edit");
  const state = useQuery({ queryKey: ["general-context"], queryFn: api.generalContext.state });
  if (state.isPending) return <Loading />;
  if (state.isError) return <ErrorState error={state.error} />;
  const s = state.data;
  return (
    <>
      <PageHeader title={t("gc.title")} description={t("gc.description")} sacredIcon="book" />
      <StatusBar state={s} />
      <div role="tablist" aria-label={t("gc.tabs")} className="mb-4 flex flex-wrap gap-1">
        {TABS.map((name) => (
          <Button key={name} role="tab" aria-selected={tab === name} variant={tab === name ? "primary" : "ghost"} size="sm" onClick={() => setTab(name)}>
            {t(`gc.tab.${name}`)}
            {name === "review" && s.draftEval ? (
              <span data-testid="review-tab-state" className="ml-1 rounded-full bg-surface-sunken px-1.5 text-[11px] text-secondary">
                {!s.draftEval.current ? t("gc.reviewStaleShort") : s.draftEval.passed ? t("eval.approved") : t("eval.rejected")}
              </span>
            ) : null}
            {name === "publish" && s.draft ? <span aria-hidden="true" className="ml-1 inline-block size-1.5 rounded-full bg-accent" /> : null}
          </Button>
        ))}
      </div>
      {tab === "edit" ? <EditorTab state={s} /> : null}
      {tab === "preview" ? <PreviewTab state={s} /> : null}
      {tab === "review" ? <ReviewTab state={s} /> : null}
      {tab === "publish" ? <PublishTab state={s} /> : null}
      {tab === "history" ? <HistoryTab state={s} /> : null}
    </>
  );
}

function StatusBar({ state }: { state: GeneralContextState }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const rebase = useMutation({
    mutationFn: () => api.generalContext.rebase(state.draft!.rowVersion),
    onSuccess: () => client.invalidateQueries({ queryKey: ["general-context"] }),
  });
  const stale = state.draft !== null && state.draft.baseRevision !== (state.live?.revision ?? null);
  return (
    <Card className="mb-4">
      <CardBody className="flex flex-col gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-2" aria-label={t("gc.status")}>
          <Badge tone="info">{t("gc.environment", { environment: state.environment })}</Badge>
          {state.live
            ? <Badge tone="success">{t("gc.liveRevision", { revision: state.live.revision })}</Badge>
            : <Badge tone="warning">{t("gc.nothingLive")}</Badge>}
          {state.draft
            ? <Badge tone="accent">{t("gc.draftRevision", { revision: state.draft.revision, version: state.draft.rowVersion })}</Badge>
            : <Badge>{t("gc.noDraft")}</Badge>}
          {state.live ? AUDIENCES.map((agent) => (
            <span key={agent} className="text-muted">{t("gc.sharedSize", { audience: t(`gc.agent.${agent}`), bytes: state.live!.sharedBytes[agent], tokens: approxTokens(state.live!.sharedBytes[agent]), budget: state.budgetBytes })}</span>
          )) : null}
        </div>
        {stale ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-danger">
            <span>{t("gc.staleDraft", { base: state.draft!.baseRevision ?? "—", live: state.live?.revision ?? "—" })}</span>
            <Button size="sm" variant="secondary" onClick={() => rebase.mutate()} disabled={rebase.isPending}>{t("gc.rebase")}</Button>
          </div>
        ) : null}
        {rebase.isError ? <p role="alert" className="text-danger">{errorText(rebase.error)}</p> : null}
      </CardBody>
    </Card>
  );
}

function useDraftContent(state: GeneralContextState) {
  const source = state.draft?.revision ?? state.live?.revision ?? null;
  return useQuery({
    queryKey: ["general-context", "content", source, state.draft?.rowVersion ?? 0],
    queryFn: async () => (source === null ? emptyContent() : (await api.generalContext.revision(source)).content),
  });
}

function EditorTab({ state }: { state: GeneralContextState }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const loaded = useDraftContent(state);
  const policies = useQuery({ queryKey: ["policies", "live"], queryFn: () => api.listPolicies({ state: "live" }) });
  const livePolicies = policies.data ?? [];
  const [content, setContent] = useState<GeneralContextContent | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  useEffect(() => { if (loaded.data) { setContent(structuredClone(loaded.data)); setSavedSnapshot(null); } }, [loaded.data]);
  const save = useMutation({
    mutationFn: (value: GeneralContextContent) => api.generalContext.saveDraft(value, state.draft?.rowVersion ?? null),
    onSuccess: (_saved, value) => { setSavedSnapshot(JSON.stringify(value)); void client.invalidateQueries({ queryKey: ["general-context"] }); },
  });
  const discard = useMutation({
    mutationFn: () => api.generalContext.discard(state.draft!.rowVersion),
    onSuccess: () => client.invalidateQueries({ queryKey: ["general-context"] }),
  });
  if (loaded.isError) return <ErrorState error={loaded.error} />;
  if (loaded.isPending || !content) return <Loading />;
  const dirty = JSON.stringify(content) !== (savedSnapshot ?? JSON.stringify(loaded.data));
  const update = (patch: Partial<GeneralContextContent>) => setContent({ ...content, ...patch });
  const setTerm = (index: number, patch: Partial<GlossaryEntry>) =>
    update({ glossary: content.glossary.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)) });
  const setDetail = (index: number, patch: Partial<GeneralContextDetail>) =>
    update({ details: content.details.map((detail, i) => (i === index ? { ...detail, ...patch } : detail)) });
  return (
    <div className="flex flex-col gap-4">
      {!state.draft ? <p className="text-muted text-sm">{t(state.live ? "gc.startFromLive" : "gc.startEmpty")}</p> : null}
      <Card>
        <CardHeader><CardTitle>{t("gc.base")}</CardTitle></CardHeader>
        <CardBody className="flex flex-col gap-3">
          <p className="text-muted text-xs">{t("gc.baseHint")}</p>
          <div className="grid gap-4 lg:grid-cols-2">
            {AUDIENCES.map((agent) => {
              const bytes = utf8Bytes(content.base[agent]) + PREAMBLE_BYTES;
              const over = bytes > state.budgetBytes;
              return (
                <div key={agent} className="flex flex-col gap-1.5">
                  <Field label={t(`gc.base.${agent}`)} htmlFor={`base-${agent}`} hint={t("gc.mentionHint")}>
                    <PolicyMentionTextarea id={`base-${agent}`} label={t(`gc.base.${agent}`)} className="min-h-56 font-mono" policies={livePolicies}
                      value={content.base[agent]} onChange={(text) => update({ base: { ...content.base, [agent]: text } })} />
                  </Field>
                  <div className="flex items-center gap-2" aria-live="polite">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-sunken" aria-hidden="true">
                      <div className={over ? "h-full bg-danger" : "h-full bg-accent"} style={{ width: `${Math.min(100, (bytes / state.budgetBytes) * 100)}%` }} />
                    </div>
                    <span data-testid={`meter-${agent}`} className={over ? "text-danger text-xs" : "text-muted text-xs"}>
                      {t("gc.meter", { bytes, budget: state.budgetBytes, tokens: approxTokens(bytes) })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("gc.glossary")}</CardTitle>
          <Button size="sm" variant="secondary" onClick={() => update({ glossary: [...content.glossary, { term: "", meaning: "", variants: [], preferred: null, examples: [] }] })}>
            <Plus size={14} aria-hidden="true" />{t("gc.addTerm")}
          </Button>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          <p className="text-muted text-xs">{t("gc.glossaryHint")}</p>
          {content.glossary.map((entry, index) => (
            <fieldset key={index} aria-label={t("gc.termN", { n: index + 1 })} className="border-border-subtle grid gap-3 rounded-lg border p-3 md:grid-cols-2">
              <Field label={t("gc.term")} htmlFor={`term-${index}`}><Input id={`term-${index}`} value={entry.term} onChange={(e) => setTerm(index, { term: e.target.value })} /></Field>
              <Field label={t("gc.variants")} htmlFor={`variants-${index}`} hint={t("gc.commaHint")}>
                <Input id={`variants-${index}`} value={entry.variants.join(", ")} onChange={(e) => setTerm(index, { variants: commas(e.target.value) })} />
              </Field>
              <Field label={t("gc.meaning")} htmlFor={`meaning-${index}`}><Textarea id={`meaning-${index}`} value={entry.meaning} onChange={(e) => setTerm(index, { meaning: e.target.value })} /></Field>
              <Field label={t("gc.preferred")} htmlFor={`preferred-${index}`}>
                <Textarea id={`preferred-${index}`} value={entry.preferred ?? ""} onChange={(e) => setTerm(index, { preferred: e.target.value.trim() ? e.target.value : null })} />
              </Field>
              <Field label={t("gc.examples")} htmlFor={`examples-${index}`} hint={t("gc.lineHint")}>
                <Textarea id={`examples-${index}`} value={entry.examples.join("\n")} onChange={(e) => setTerm(index, { examples: lines(e.target.value) })} />
              </Field>
              <div className="flex items-end justify-end">
                <Button size="sm" variant="ghost" onClick={() => update({ glossary: content.glossary.filter((_, i) => i !== index) })}>
                  <Trash2 size={14} aria-hidden="true" />{t("gc.remove")}
                </Button>
              </div>
            </fieldset>
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("gc.details")}</CardTitle>
          <Button size="sm" variant="secondary" onClick={() => update({ details: [...content.details, { slug: "", title: "", body: "", audience: { permissions: [], agents: "staff" } }] })}>
            <Plus size={14} aria-hidden="true" />{t("gc.addDetail")}
          </Button>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          <p className="text-muted text-xs">{t("gc.detailsHint")}</p>
          {content.details.map((detail, index) => (
            <fieldset key={index} aria-label={t("gc.detailN", { n: index + 1 })} className="border-border-subtle grid gap-3 rounded-lg border p-3 md:grid-cols-2">
              <Field label={t("gc.slug")} htmlFor={`slug-${index}`}><Input id={`slug-${index}`} value={detail.slug} onChange={(e) => setDetail(index, { slug: e.target.value })} /></Field>
              <Field label={t("gc.detailTitle")} htmlFor={`title-${index}`}><Input id={`title-${index}`} value={detail.title} onChange={(e) => setDetail(index, { title: e.target.value })} /></Field>
              <Field label={t("gc.permissions")} htmlFor={`permissions-${index}`} hint={t("gc.permissionsHint")}>
                <Input id={`permissions-${index}`} value={detail.audience.permissions.join(", ")} onChange={(e) => setDetail(index, { audience: { ...detail.audience, permissions: commas(e.target.value) } })} />
              </Field>
              <Field label={t("gc.agents")} htmlFor={`agents-${index}`}>
                <Select id={`agents-${index}`} value={detail.audience.agents} onChange={(e) => setDetail(index, { audience: { ...detail.audience, agents: e.target.value as AgentAudience } })}>
                  {(["staff", "rider", "all"] as const).map((value) => <option key={value} value={value}>{t(`gc.agent.${value}`)}</option>)}
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Field label={t("gc.detailBody")} htmlFor={`body-${index}`} hint={t("gc.mentionHint")}>
                  <PolicyMentionTextarea id={`body-${index}`} label={t("gc.detailBody")} className="min-h-48 font-mono" policies={livePolicies} value={detail.body} onChange={(body) => setDetail(index, { body })} />
                </Field>
              </div>
              <div className="flex justify-end md:col-span-2">
                <Button size="sm" variant="ghost" onClick={() => update({ details: content.details.filter((_, i) => i !== index) })}>
                  <Trash2 size={14} aria-hidden="true" />{t("gc.remove")}
                </Button>
              </div>
            </fieldset>
          ))}
        </CardBody>
      </Card>
      <div className="border-border bg-surface sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 shadow-sm">
        <Button onClick={() => save.mutate(content)} disabled={save.isPending || !dirty && Boolean(state.draft)}>{t("gc.saveDraft")}</Button>
        {state.draft ? <Button variant="danger" onClick={() => discard.mutate()} disabled={discard.isPending}>{t("gc.discard")}</Button> : null}
        {dirty ? <Badge tone="warning">{t("gc.unsaved")}</Badge> : save.isSuccess ? <span role="status" className="text-sm">{t("gc.saved")}</span> : null}
      </div>
      {save.isError ? <p role="alert" className="text-danger text-sm">{save.error instanceof ApiError && save.error.status === 409 ? t("gc.conflict") : errorText(save.error)}</p> : null}
      {discard.isError ? <p role="alert" className="text-danger text-sm">{errorText(discard.error)}</p> : null}
    </div>
  );
}

function PreviewTab({ state }: { state: GeneralContextState }) {
  const { t } = useTranslation();
  const [target, setTarget] = useState<"draft" | "live">(state.draft ? "draft" : "live");
  const [permissions, setPermissions] = useState("");
  const [agent, setAgent] = useState<"rider" | "staff">("rider");
  const enabled = target === "draft" ? state.draft !== null : state.live !== null;
  const preview = useQuery({
    queryKey: ["general-context", "preview", target, permissions, agent, state.draft?.rowVersion ?? 0, state.live?.revision ?? 0],
    queryFn: () => api.generalContext.preview(target, commas(permissions), agent),
    enabled,
  });
  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t("gc.previewHint")}</p>
      <div className="grid gap-3 md:grid-cols-3">
        <Field label={t("gc.previewTarget")} htmlFor="preview-target">
          <Select id="preview-target" value={target} onChange={(e) => setTarget(e.target.value as "draft" | "live")}>
            <option value="draft">{t("gc.targetDraft")}</option>
            <option value="live">{t("gc.targetLive")}</option>
          </Select>
        </Field>
        <Field label={t("gc.previewPermissions")} htmlFor="preview-permissions" hint={t("gc.commaHint")}>
          <Input id="preview-permissions" value={permissions} onChange={(e) => setPermissions(e.target.value)} placeholder="RIDER" />
        </Field>
        <Field label={t("gc.previewAgent")} htmlFor="preview-agent">
          <Select id="preview-agent" value={agent} onChange={(e) => setAgent(e.target.value as "rider" | "staff")}>
            <option value="rider">{t("gc.agent.rider")}</option>
            <option value="staff">{t("gc.agent.staff")}</option>
          </Select>
        </Field>
      </div>
      {!enabled ? <p className="text-muted text-sm">{t("gc.nothingToPreview")}</p> : null}
      {preview.isError ? <ErrorState error={preview.error} /> : null}
      {preview.data ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t("gc.sharedBlock")}</CardTitle>
              <Badge tone={preview.data.shared.withinBudget ? "success" : "danger"}>
                {t("gc.budget", { bytes: preview.data.shared.bytes, budget: preview.data.shared.budgetBytes })}
              </Badge>
            </CardHeader>
            <CardBody>
              {!preview.data.shared.withinBudget ? <p role="alert" className="text-danger mb-2 text-sm">{t("gc.overBudget")}</p> : null}
              <pre aria-label={t("gc.sharedBlock")} className="bg-surface-sunken overflow-x-auto rounded-md p-3 text-xs whitespace-pre-wrap">{preview.data.shared.text}</pre>
            </CardBody>
          </Card>
          <Card>
            <CardHeader><CardTitle>{t("gc.visibleDetails")}</CardTitle><Badge>{t("gc.hiddenCount", { count: preview.data.hiddenDetails })}</Badge></CardHeader>
            <CardBody className="flex flex-col gap-3">
              {preview.data.details.length === 0 ? <p className="text-muted text-sm">{t("gc.noVisibleDetails")}</p> : null}
              {preview.data.details.map((detail) => (
                <pre key={detail.slug} aria-label={detail.title} className="bg-surface-sunken overflow-x-auto rounded-md p-3 text-xs whitespace-pre-wrap">{detail.text}</pre>
              ))}
            </CardBody>
          </Card>
        </>
      ) : null}
    </div>
  );
}

export function GeneralContextReportView({ report }: { report: GeneralContextReport }) {
  const { t } = useTranslation();
  return (
    <section aria-label={t("gc.reviewRegion")} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={report.passed ? "success" : "danger"}>{report.passed ? t("eval.approved") : t("eval.rejected")}</Badge>
        <span className="text-muted text-xs">{t("eval.judgeVersion", { version: report.judgeVersion })}</span>
      </div>
      <ul className="flex flex-col gap-2">
        {CHECKS.map((name) => {
          const check = report.checks.find((candidate) => candidate.name === name);
          return (
            <li key={name} className="border-border-subtle rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  {check?.passed ? <Check size={16} aria-hidden="true" /> : <X size={16} aria-hidden="true" className="text-danger" />}
                  {t(`gc.check.${name}`)}
                </span>
                <Badge tone={check?.passed ? "success" : "danger"}>{check?.passed ? t("eval.passed") : t("eval.failed")}</Badge>
              </div>
              {check && check.details.length ? <ul className="text-secondary mt-2 list-disc pl-5 text-sm">{check.details.map((detail, i) => <li key={i}>{detail}</li>)}</ul> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ReviewTab({ state }: { state: GeneralContextState }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const evaluate = useMutation({
    mutationFn: api.generalContext.evaluate,
    onSettled: () => client.invalidateQueries({ queryKey: ["general-context"] }),
  });
  if (!state.draft) return <p className="text-muted text-sm">{t("gc.noDraftToReview")}</p>;
  const last = state.draftEval;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t("gc.reviewHint")}</p>
      <div><Button onClick={() => evaluate.mutate()} disabled={evaluate.isPending}>{evaluate.isPending ? t("gc.reviewing") : t("gc.askReview")}</Button></div>
      {evaluate.isError ? <p role="alert" className="text-danger text-sm">{errorText(evaluate.error)}</p> : null}
      {last ? (
        <Card>
          <CardHeader>
            <CardTitle sacredIcon="quill">{t("gc.lastReview", { at: formatDateTime(last.at) })}</CardTitle>
            {last.current ? <Badge tone="info">{t("gc.reviewCurrent")}</Badge> : <Badge tone="warning">{t("gc.reviewStale")}</Badge>}
          </CardHeader>
          <CardBody><GeneralContextReportView report={last.report} /></CardBody>
        </Card>
      ) : <p className="text-muted text-sm">{t("gc.noReviewYet")}</p>}
    </div>
  );
}

function PublishTab({ state }: { state: GeneralContextState }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [confirm, setConfirm] = useState("");
  const diff = useQuery({ queryKey: ["general-context", "diff", state.draft?.rowVersion ?? 0, state.live?.revision ?? 0], queryFn: api.generalContext.diff, enabled: state.draft !== null });
  const publish = useMutation({
    mutationFn: () => api.generalContext.publish(state.draft!.rowVersion, state.live?.revision ?? null),
    onSettled: () => client.invalidateQueries({ queryKey: ["general-context"] }),
  });
  const result = publish.data ? (
    <Card>
      <CardHeader>
        <CardTitle>{publish.data.outcome === "published" ? t("gc.published", { revision: publish.data.revision }) : t("gc.rejected")}</CardTitle>
      </CardHeader>
      <CardBody><GeneralContextReportView report={publish.data.report} /></CardBody>
    </Card>
  ) : null;
  // After a publication the draft is gone; the result must stay visible.
  if (!state.draft) return <div className="flex flex-col gap-4">{result}<p className="text-muted text-sm">{t("gc.noDraftToPublish")}</p></div>;
  const names = (items: string[]) => (items.length ? items.join(", ") : "—");
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader><CardTitle>{t("gc.changes", { live: state.live?.revision ?? "—", draft: state.draft.revision })}</CardTitle></CardHeader>
        <CardBody className="text-sm">
          {diff.isError ? <ErrorState error={diff.error} /> : null}
          {diff.data ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt>{t("gc.diffTermsAdded")}</dt><dd>{names(diff.data.glossary.added)}</dd>
              <dt>{t("gc.diffTermsRemoved")}</dt><dd>{names(diff.data.glossary.removed)}</dd>
              <dt>{t("gc.diffTermsChanged")}</dt><dd>{names(diff.data.glossary.changed)}</dd>
              <dt>{t("gc.diffBaseRider")}</dt><dd>{diff.data.base.rider.changed ? t("gc.changed") : t("gc.unchanged")}</dd>
              <dt>{t("gc.diffBaseStaff")}</dt><dd>{diff.data.base.staff.changed ? t("gc.changed") : t("gc.unchanged")}</dd>
              <dt>{t("gc.diffDetailsAdded")}</dt><dd>{names(diff.data.details.added)}</dd>
              <dt>{t("gc.diffDetailsRemoved")}</dt><dd>{names(diff.data.details.removed)}</dd>
              <dt>{t("gc.diffDetailsChanged")}</dt><dd>{names(diff.data.details.changed)}</dd>
            </dl>
          ) : null}
        </CardBody>
      </Card>
      <p className="text-sm">{t("gc.publishHint", { environment: state.environment })}</p>
      <Field label={t("gc.confirmEnvironment", { environment: state.environment })} htmlFor="confirm-environment">
        <Input id="confirm-environment" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
      <div>
        <Button onClick={() => publish.mutate()} disabled={confirm !== state.environment || publish.isPending}>
          {publish.isPending ? t("gc.publishing") : t("gc.publish", { environment: state.environment })}
        </Button>
      </div>
      {publish.isError ? <p role="alert" className="text-danger text-sm">{errorText(publish.error)}</p> : null}
      {result}
    </div>
  );
}

function HistoryTab({ state }: { state: GeneralContextState }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const history = useQuery({ queryKey: ["general-context", "revisions", state.live?.revision ?? 0], queryFn: api.generalContext.revisions });
  const [withdrawing, setWithdrawing] = useState(false);
  const [exported, setExported] = useState("");
  const [imported, setImported] = useState("");
  const refresh = () => client.invalidateQueries({ queryKey: ["general-context"] });
  const restore = useMutation({ mutationFn: (revision: number) => api.generalContext.restore(revision), onSuccess: refresh });
  const withdraw = useMutation({
    mutationFn: (reason: string) => api.generalContext.withdraw(state.live!.revision, reason),
    onSuccess: () => { setWithdrawing(false); void refresh(); },
  });
  const exportLive = useMutation({ mutationFn: api.generalContext.exportLive, onSuccess: (data) => setExported(JSON.stringify(data, null, 2)) });
  const importDraft = useMutation({
    mutationFn: () => api.generalContext.importDraft(JSON.parse(imported) as GeneralContextExport, state.live?.revision ?? null),
    onSuccess: () => { setImported(""); void refresh(); },
  });
  const failure = [restore, withdraw, exportLive, importDraft].find((mutation) => mutation.isError)?.error;
  return (
    <div className="flex flex-col gap-4">
      {failure ? <p role="alert" className="text-danger text-sm">{errorText(failure)}</p> : null}
      <Card>
        <CardHeader><CardTitle>{t("gc.revisions")}</CardTitle>
          {state.live ? <Button size="sm" variant="danger" onClick={() => setWithdrawing(true)}>{t("gc.withdraw")}</Button> : null}
        </CardHeader>
        <CardBody>
          {history.isPending ? <Loading /> : history.isError ? <ErrorState error={history.error} /> : (
            <ul aria-label={t("gc.revisions")} className="flex flex-col gap-2">
              {history.data.map((row: GeneralContextRevision) => (
                <li key={row.revision} className="border-border-subtle flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm">
                  <span className="flex flex-wrap items-center gap-2">
                    <strong>{t("gc.revisionN", { revision: row.revision })}</strong>
                    <Badge tone={row.status === "published" ? "success" : row.status === "withdrawn" ? "danger" : "neutral"}>{t(`gc.status.${row.status}`)}</Badge>
                    {row.publishedAt ? <span className="text-muted">{t("gc.publishedBy", { at: formatDateTime(row.publishedAt), by: row.publishedBy })}</span> : null}
                    {row.origin ? <span className="text-muted">{t(`gc.origin.${row.origin.kind}`, { environment: row.origin.environment, revision: row.origin.revision })}</span> : null}
                    {row.closedReason ? <span className="text-muted">{t("gc.reason", { reason: row.closedReason })}</span> : null}
                  </span>
                  {row.status === "superseded" ? (
                    <Button size="sm" variant="secondary" onClick={() => restore.mutate(row.revision)} disabled={restore.isPending || state.draft !== null}>{t("gc.restore")}</Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("gc.promotion")}</CardTitle></CardHeader>
        <CardBody className="flex flex-col gap-3">
          <p className="text-muted text-sm">{t("gc.promotionHint")}</p>
          <div><Button size="sm" variant="secondary" onClick={() => exportLive.mutate()} disabled={!state.live}>{t("gc.export")}</Button></div>
          {exported ? <Textarea aria-label={t("gc.exported")} readOnly className="min-h-40 font-mono text-xs" value={exported} /> : null}
          <Field label={t("gc.importLabel", { live: state.live?.revision ?? "—" })} htmlFor="import-json">
            <Textarea id="import-json" className="min-h-32 font-mono text-xs" value={imported} onChange={(e) => setImported(e.target.value)} />
          </Field>
          <div><Button size="sm" onClick={() => importDraft.mutate()} disabled={!imported.trim() || state.draft !== null || importDraft.isPending}>{t("gc.import")}</Button></div>
        </CardBody>
      </Card>
      <ReasonDialog
        open={withdrawing}
        title={t("gc.withdrawTitle", { revision: state.live?.revision ?? "—" })}
        onCancel={() => setWithdrawing(false)}
        onConfirm={(reason) => withdraw.mutate(reason)}
        pending={withdraw.isPending}
      />
    </div>
  );
}
